import { useCallback, useState } from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { DiffPreviewDialog } from '@/components/layout/diff-preview-dialog'
import { fetchWithAuth } from '@/lib/api'
import { getGitSourceFileContent, resolveGitSourceConflict } from '@/lib/api/git-sources'
import { testIds } from '@/lib/test-ids'
import type { Thread } from './types'

type GitConflictMeta = {
  kind?: unknown
  file_path?: unknown
  git_source_id?: unknown
  db_document_id?: unknown
  resolved?: unknown
}

function readMeta(thread: Thread): {
  filePath: string
  gitSourceId: string
  dbDocumentId: string | undefined
  filename: string
} | null {
  const meta = thread.metadata as GitConflictMeta | undefined
  if (!meta) return null
  const filePath = typeof meta.file_path === 'string' ? meta.file_path : ''
  const gitSourceId = typeof meta.git_source_id === 'string' ? meta.git_source_id : ''
  const dbDocumentId = typeof meta.db_document_id === 'string' ? meta.db_document_id : undefined
  if (!filePath || !gitSourceId) return null
  const parts = filePath.split('/').filter(Boolean)
  const filename = parts.length ? (parts[parts.length - 1] as string) : filePath
  return { filePath, gitSourceId, dbDocumentId, filename }
}

export function GitConflictActionBar({
  thread,
  onResolved,
}: {
  thread: Thread
  onResolved?: () => void
}) {
  const parsed = readMeta(thread)
  const [diffOpen, setDiffOpen] = useState(false)
  const [flownotesContent, setFlownotesContent] = useState<string | null>(null)
  const [upstreamContent, setUpstreamContent] = useState<string | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [resolving, setResolving] = useState(false)

  const loadDiffContent = useCallback(async () => {
    if (!parsed) return
    setLoadError(null)
    try {
      const docUrl = parsed.dbDocumentId
        ? `/api/documents/${parsed.dbDocumentId}`
        : `/api/documents/by-path?path=${encodeURIComponent(parsed.filePath)}`
      const [docRes, upstream] = await Promise.all([
        fetchWithAuth(docUrl),
        getGitSourceFileContent(parsed.gitSourceId, parsed.filePath),
      ])
      if (!docRes.ok) {
        const err = await docRes.json().catch(() => ({}))
        throw new Error((err as { error?: string }).error ?? 'Could not load Maater document')
      }
      const docJson = (await docRes.json()) as { content?: string }
      if (typeof docJson.content !== 'string') {
        throw new Error('Document response missing content')
      }
      setFlownotesContent(docJson.content)
      setUpstreamContent(upstream)
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Could not load diff'
      setLoadError(msg)
      toast.error(msg)
    }
  }, [parsed])

  const handleOpenDiff = useCallback(async () => {
    setDiffOpen(true)
    await loadDiffContent()
  }, [loadDiffContent])

  const finalize = async (resolution: 'upstream' | 'flownotes' | 'ai', aiContent?: string) => {
    if (!parsed) return
    setResolving(true)
    try {
      await resolveGitSourceConflict({
        gitSourceId: parsed.gitSourceId,
        filePath: parsed.filePath,
        resolution,
        threadId: thread.id,
        dbDocumentId: parsed.dbDocumentId,
        ...(resolution === 'ai' && aiContent != null ? { aiContent } : {}),
      })
      toast.success(resolution === 'flownotes' ? 'Keeping Maater version' : resolution === 'upstream' ? 'Accepted upstream' : 'Resolution saved')
      setDiffOpen(false)
      onResolved?.()
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Resolution failed')
    } finally {
      setResolving(false)
    }
  }

  if (!parsed) return null

  const original = flownotesContent ?? ''
  const proposed = upstreamContent ?? ''

  return (
    <div
      className="flex flex-wrap shrink-0 items-center gap-2 border-b border-border bg-amber-500/10 px-4 py-3"
      data-testid={testIds.inbox.gitConflictBar}
    >
      <span className="text-sm font-medium text-amber-950 dark:text-amber-200">
        Resolve Git conflict:
      </span>
      <Button
        type="button"
        size="sm"
        variant="default"
        disabled={resolving}
        data-testid={testIds.inbox.gitConflictAcceptUpstream}
        onClick={() => void finalize('upstream')}
      >
        Accept Upstream
      </Button>
      <Button
        type="button"
        size="sm"
        variant="outline"
        disabled={resolving}
        data-testid={testIds.inbox.gitConflictKeepFlowNotes}
        onClick={() => void finalize('flownotes')}
      >
        Keep Maater
      </Button>
      <Button type="button" size="sm" variant="secondary" data-testid={testIds.inbox.gitConflictViewDiff} onClick={() => void handleOpenDiff()}>
        View Diff
      </Button>
      {loadError ? <span className="text-xs text-destructive">{loadError}</span> : null}

      <DiffPreviewDialog
        open={diffOpen}
        onOpenChange={setDiffOpen}
        original={original}
        proposed={proposed}
        filename={parsed.filename}
        conflictMode
        onAcceptUpstream={() => void finalize('upstream')}
        onKeepFlowNotes={() => void finalize('flownotes')}
        isApplying={resolving}
        onAccept={() => {}}
        onReject={() => setDiffOpen(false)}
      />
    </div>
  )
}
